/** Casting / Auditions Desk cubicle. Side-by-side script breakdown
 *  panel + actor profile cards with rating sliders + video players. */
import { useState } from "react";
import { Plus, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import useCubicleRecords from "./useCubicleRecords";

export default function CastingCubicle({ projectId }) {
  const scenes  = useCubicleRecords(projectId, "auditions", "casting_scene");
  const actors  = useCubicleRecords(projectId, "auditions", "actor_profile");
  const [sceneDialog, setSceneDialog] = useState(false);
  const [actorDialog, setActorDialog] = useState(false);
  const [scForm, setScForm] = useState({ title: "", page: "", description: "" });
  const [acForm, setAcForm] = useState({
    name: "", role: "", rating: 3, tags: "",
    headshot_url: "", audition_video_url: "", notes: "",
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,4fr)]"
         data-testid="casting-cubicle">
      {/* Script Breakdown Panel */}
      <section className="cs-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Script Breakdown
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {scenes.items.length} scene(s)
            </div>
          </div>
          <Button onClick={() => setSceneDialog(true)}
                  className="h-8 gap-1 bg-white text-black hover:bg-white/90"
                  data-testid="casting-add-scene">
            <Plus size={13}/> Scene
          </Button>
        </div>
        {scenes.items.length === 0 ? (
          <div className="text-sm text-white/45 py-8 text-center">
            Add scenes to build the shot-by-shot breakdown.
          </div>
        ) : (
          <ol className="space-y-2" data-testid="casting-scene-list">
            {scenes.items.map((s, i) => (
              <li key={s.id}
                  className="rounded-lg border border-white/10 bg-black/30 p-3 flex gap-3"
                  data-testid={`scene-row-${s.id}`}>
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 shrink-0 w-10 pt-0.5">
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium">
                    {s.data?.title || "Untitled scene"}
                    {s.data?.page && <span className="ml-2 text-white/45 text-[11px]">p.{s.data.page}</span>}
                  </div>
                  {s.data?.description && (
                    <div className="text-xs text-white/60 mt-0.5">{s.data.description}</div>
                  )}
                </div>
                <Button variant="ghost" onClick={() => scenes.remove(s.id)}
                        className="h-7 px-2 text-white/45 hover:text-rose-300"
                        data-testid={`scene-remove-${s.id}`}>
                  <Trash2 size={13}/>
                </Button>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Actor Profiles */}
      <section className="cs-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Actor Profiles
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {actors.items.length} candidate(s)
            </div>
          </div>
          <Button onClick={() => setActorDialog(true)}
                  className="h-8 gap-1 bg-white text-black hover:bg-white/90"
                  data-testid="casting-add-actor">
            <Plus size={13}/> Actor
          </Button>
        </div>
        {actors.items.length === 0 ? (
          <div className="text-sm text-white/45 py-8 text-center">
            No candidates yet. Add an actor profile with an audition video.
          </div>
        ) : (
          <div className="space-y-3" data-testid="casting-actor-list">
            {actors.items.map((a) => <ActorCard key={a.id} record={a} onPatch={actors.patch} onRemove={actors.remove}/>)}
          </div>
        )}
      </section>

      {/* Scene dialog */}
      <Dialog open={sceneDialog} onOpenChange={setSceneDialog}>
        <DialogContent className="max-w-md bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">Add Scene</DialogTitle>
            <DialogDescription className="sr-only">Script breakdown scene entry.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={scForm.title} onChange={(e) => setScForm({ ...scForm, title: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="scene-title-input"/>
            </div>
            <div>
              <Label>Page</Label>
              <Input value={scForm.page} onChange={(e) => setScForm({ ...scForm, page: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={scForm.description}
                        onChange={(e) => setScForm({ ...scForm, description: e.target.value })}
                        className="mt-1 bg-black/40 border-white/15"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSceneDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (!scForm.title.trim()) return;
              await scenes.create("casting_scene", scForm);
              setSceneDialog(false);
              setScForm({ title: "", page: "", description: "" });
            }} className="bg-white text-black hover:bg-white/90"
               data-testid="scene-submit">Add scene</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actor dialog */}
      <Dialog open={actorDialog} onOpenChange={setActorDialog}>
        <DialogContent className="max-w-lg bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">Add Actor</DialogTitle>
            <DialogDescription className="sr-only">Actor profile entry.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label>
              <Input value={acForm.name} onChange={(e) => setAcForm({ ...acForm, name: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="actor-name-input"/></div>
            <div><Label>Role</Label>
              <Input value={acForm.role} onChange={(e) => setAcForm({ ...acForm, role: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/></div>
            <div className="col-span-2">
              <Label>Headshot URL</Label>
              <Input value={acForm.headshot_url}
                     onChange={(e) => setAcForm({ ...acForm, headshot_url: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     placeholder="https://…/actor.jpg"/></div>
            <div className="col-span-2">
              <Label>Audition Video URL</Label>
              <Input value={acForm.audition_video_url}
                     onChange={(e) => setAcForm({ ...acForm, audition_video_url: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     placeholder="https://…/reel.mp4"/></div>
            <div className="col-span-2">
              <Label>Tags (comma-separated)</Label>
              <Input value={acForm.tags} onChange={(e) => setAcForm({ ...acForm, tags: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     placeholder="Hindi, Marathi, Improv"/></div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea value={acForm.notes}
                        onChange={(e) => setAcForm({ ...acForm, notes: e.target.value })}
                        className="mt-1 bg-black/40 border-white/15"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActorDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (!acForm.name.trim()) return;
              await actors.create("actor_profile", {
                ...acForm,
                rating: Number(acForm.rating) || 3,
                tags: acForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
              });
              setActorDialog(false);
              setAcForm({ name: "", role: "", rating: 3, tags: "",
                          headshot_url: "", audition_video_url: "", notes: "" });
            }} className="bg-white text-black hover:bg-white/90"
               data-testid="actor-submit">Add actor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ActorCard({ record, onPatch, onRemove }) {
  const d = record.data || {};
  const rating = Number(d.rating) || 0;
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4"
         data-testid={`actor-card-${record.id}`}>
      <div className="flex gap-3">
        <div className="w-14 h-14 rounded-lg bg-white/10 overflow-hidden shrink-0 flex items-center justify-center">
          {d.headshot_url ? (
            <img src={d.headshot_url} alt="" className="w-full h-full object-cover"
                 onError={(e) => { e.currentTarget.style.display = "none"; }}/>
          ) : <div className="text-white/40 text-xs">HEAD</div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-white truncate">{d.name || "Unnamed"}</div>
            {d.role && <div className="text-[10px] tracking-[0.22em] uppercase text-white/55">· {d.role}</div>}
          </div>
          {(d.tags || []).length > 0 && (
            <div className="mt-1 flex gap-1 flex-wrap">
              {d.tags.map((t, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/70">{t}</span>
              ))}
            </div>
          )}
        </div>
        <Button variant="ghost" onClick={() => onRemove(record.id)}
                className="h-7 px-2 text-white/45 hover:text-rose-300 shrink-0"
                data-testid={`actor-remove-${record.id}`}>
          <Trash2 size={13}/>
        </Button>
      </div>

      {d.audition_video_url && (
        <video src={d.audition_video_url} controls
               className="mt-3 w-full rounded-lg"
               data-testid={`actor-video-${record.id}`}/>
      )}

      <div className="mt-3 flex items-center gap-3">
        <Star size={12} className="text-amber-300"/>
        <input type="range" min={0} max={5} step={1}
               value={rating}
               onChange={(e) => onPatch(record.id, { rating: Number(e.target.value) })}
               className="flex-1 accent-amber-300"
               data-testid={`actor-rating-${record.id}`}/>
        <span className="text-xs text-white/70 w-10 text-right"
              data-testid={`actor-rating-value-${record.id}`}>
          {rating}/5
        </span>
      </div>

      {d.notes && <div className="text-xs text-white/60 mt-2">{d.notes}</div>}
    </div>
  );
}
